import { INote } from '../caremap/ICareMap';
import { CareMapState } from '../caremap/CareMapState';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { State } from '@meraki-internal/state';
import { WithLinks } from '../caremap/WithLinks';

export class NoteSavingHelper extends State<{}> {
    static inject= () => [CareMapState, APIClient];
    constructor(private careMapState: CareMapState, private apiClient: APIClient){
        super({});
    }

    private savingPromises: {
        [noteId: string]: Promise<void> | undefined;
    } = {};
    private savingQueue: {
        [noteId: string]: { note: WithLinks<INote>, action: 'update' | 'delete' } | undefined
    } = {};
    private savingError: {
        [noteId: string]: Error | undefined
    } = {};

    isSaving = ({ noteId }: { noteId: string; }) => Boolean(this.savingPromises[noteId]);

    getError = ({ noteId }: { noteId: string; }) => this.savingError[noteId];

    clearError = ({ noteId, error }: { noteId: string; error: Error; }) => {
        if (this.savingError[noteId] === error){
            this.savingError[noteId] = undefined;
        }
    };

    private update = async (notesToSave: WithLinks<INote>) => {
        const link = notesToSave.links.self;

        const { version: newVersion } = await this.apiClient.put(link, {
            ...notesToSave,
            version: this.careMapState.state!.version
        });

        this.careMapState.setVersion(newVersion);
    };

    private delete = async (noteToDelete: WithLinks<INote>) => {
        const link = noteToDelete.links.delete;

        const { version: newVersion } = await this.apiClient.delete(link, {
            version: this.careMapState.state!.version
        });

        this.careMapState.setVersion(newVersion);
    };

    enqueueSave = (notesToSave: WithLinks<INote>) => {
        this.savingQueue[notesToSave.noteId] = { note: notesToSave, action: 'update' };

        this.tryDequeueSave({ noteId: notesToSave.noteId });
    };

    enqueueDelete = (noteToDelete: WithLinks<INote>) => {
        this.savingQueue[noteToDelete.noteId] = { note: noteToDelete, action: 'delete' };

        this.tryDequeueSave({ noteId: noteToDelete.noteId });
    };

    private tryDequeueSave = ({ noteId }: { noteId: string;}) => {
        if (this.savingPromises[noteId]){
            return;
        }
        if (!this.savingQueue[noteId]){
            return;
        }
        this.savingPromises[noteId] = Promise.resolve()
            .then(async () => {
                this.savingError[noteId] = undefined;

                // dequeue it
                const { note, action } = this.savingQueue[noteId]!;
                this.savingQueue[noteId] = undefined;

                // save it
                if (action === 'update') {
                    await this.update(note);
                } else if (action === 'delete') {
                    await this.delete(note);
                } else {
                    throw new Error('unsupported action ' + action);
                }
            })
            .catch(err => {
                this.savingError[noteId] = err;
            })
            .finally(() => {
                this.savingPromises[noteId] = undefined;
                this.tryDequeueSave({ noteId });

                // we may be done saving
                this.setState({});
            });
        // we're saving
        this.setState({});
    };
}
